import React from 'react';
import styled from 'styled-components';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { TagValue, ITagInputOption, StackPanel, Typography, ErrorMessage, TagInput, StackPanelOrientation, TagInputOption, IBaseComponentProps, ActionButton, typographyPresets } from '@agilepoint/wat-react-ux';
import { validateEmail } from 'common/utils';
import { CustomTooltip } from './custom-tooltip';
import { useCustomPopupEvents } from 'lib/hooks';
import { StyledActionButton, StyledPrimaryButton } from './styled-components';

const StyledTagInput = styled(TagInput)`
    width: 100%;
    border-color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.grayVariant4};
    border-radius: 6px;
    .ap-tagInput-tag {
        cursor: pointer;
        background-color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.grayVariant6};
        border-color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.grayVariant4};
        border-radius: 17px;
        :hover {
            color: ${({ theme }) => theme.semantics.palette.white};
            border-color: ${({ theme }) => theme.semantics.palette.themeDark};
            .ap-tagInput-removeTagButton {
                fill: ${({ theme }) => theme.semantics.palette.black};
            }
        }
        .ap-tagInput-tagLabel {
            color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.secondaryTextColor};
            font-size: 16px;
            max-width: unset;
        }
    }
`;

const Message = styled(Typography.Medium)`
    letter-spacing: normal;
`;

export const NegativeActionButton = styled(ActionButton)`
    color: ${({ theme: { semantics } }) => semantics.palette.red};
    height: auto;
    .ap-icon {
        fill: ${({ theme: { semantics } }) => semantics.palette.red};
    }
    .ap-button-label {
        margin: 0;
        ${typographyPresets.headings.h5};
    }
`;

interface IEmailControlProps extends Pick<IBaseComponentProps, 'className'> {
    heading?: string;
    elementOrientation?: StackPanelOrientation;
    tagInputItems?: TagValue[];
    enableClearList?: boolean;
    tooltipValue?: string;
    onChange?: (values: TagValue[], newValue?: TagInputOption | undefined) => void;
    validationErrorExists?: (value: boolean) => void;
}

export const EmailInputControl = React.memo((props: IEmailControlProps) => {
    const { heading, elementOrientation, tagInputItems, enableClearList, tooltipValue, onChange, validationErrorExists, ...rest } = props;
    const translate = useTranslate();
    const [inputItems, setTagInputItems] = React.useState<TagValue[] | undefined>(tagInputItems);
    const [isError, setError] = React.useState(false);
    const { showCustomPopup, removePopup } = useCustomPopupEvents();

    React.useEffect(() => {
        setTagInputItems(tagInputItems);
        validationErrorExists && validationErrorExists(isError);
    }, [isError, tagInputItems, validationErrorExists]);

    const onValidateItem = React.useCallback((value: ITagInputOption) => {
        const valid = validateEmail(value.text);
        if (valid) {
            setTagInputItems((prevValue) => (prevValue ? [...prevValue, value.text] : undefined));
            isError && setError(false);
        }
        else {
            setError(true);
        }
        return valid;
    }, [isError]);

    const onSearchTextChange = React.useCallback((searchText: string) => {
        if (searchText.length === 0) {
            setError(false);
        }
    }, []);

    const onRemoveItem = React.useCallback((itemValue: any) => {
        const filteredItems = inputItems?.filter((item) => item !== itemValue?.text);
        setTagInputItems(filteredItems);
    }, [inputItems]);

    const onCancelClick = React.useCallback(() => {
        removePopup('clear-list');
    }, [removePopup]);

    const onClearClick = React.useCallback(() => {
        setTagInputItems([]);
        onCancelClick();
        onChange && onChange([]);
    }, [onCancelClick, onChange]);

    const onClearList = React.useCallback(() => {
        showCustomPopup({
            headerTitle: 'CONFIRMATION',
            showModal: true,
            width: '391px',
            height: '204px',
            id: 'clear-list',
            onRenderContent: () => <Message><Localize translate='common.messages.clearList' /></Message>,
            onRenderFooter: () => (
                <>
                    <StyledActionButton label={translate('common.actions.cancel')} onClick={onCancelClick} />
                    <StyledPrimaryButton label={translate('common.actions.clear')} onClick={onClearClick} />
                </>
            )
        });
    }, [onCancelClick, onClearClick, showCustomPopup, translate]);

    return (
        <StackPanel orientation={elementOrientation} width="100%" {...rest}>
            <StackPanel alignment="center" gap="10">
                {heading ? <Typography.H5>{heading}</Typography.H5> : <></>}
                {tooltipValue ? <CustomTooltip tooltipContent={tooltipValue} /> : <></>}
            </StackPanel>
            <StackPanel orientation='vertical' width='100%' alignment="flex-end">
                {enableClearList ? <NegativeActionButton iconName='ap-delete.outline' label={translate('common.actions.clearList')} onClick={onClearList} /> : <></>}
                <StackPanel orientation='vertical' width='100%'>
                    <StyledTagInput
                        onSearchTextChange={onSearchTextChange}
                        onValidateItem={onValidateItem}
                        onRemoveItem={onRemoveItem}
                        onChange={onChange}
                        error={isError}
                        value={inputItems} />
                    {isError ? <ErrorMessage message={translate('modules.myApps.appTile.buildPopup.emailInvalidationMessage')} /> : <></>}
                </StackPanel>
            </StackPanel>
        </StackPanel>
    );
});
